import { EMOTES } from "@zend/content";
import { useEffect, useRef, useState } from "react";
import { audio } from "../audio/engine.js";
import { composeCue } from "../audio/score.js";
import { victoryCue } from "../audio/outcome.js";
import { pick, useT } from "../i18n/index.js";
import { useSession } from "../state/session.js";
import { Button } from "../ui/Button.js";
import { CosmeticModelPreview } from "./CosmeticModelPreview.js";
import { CosmeticFrame, EmoteImage } from "./cosmeticPresentation.js";
import type { CosmeticView } from "./types.js";
import "./cosmetic-preview.css";

function StingerPreview({ cosmetic }: { cosmetic: CosmeticView }) {
  const t = useT(), volume = useSession((s) => s.settings?.sound ?? 0);
  const [playing, setPlaying] = useState(false), timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cue = victoryCue(cosmetic.id), score = composeCue(cue);
  useEffect(() => () => { if (timeout.current) { clearTimeout(timeout.current); audio.stop(cue); } }, [cue]);
  return <div className="cos-stinger">
    <div className="cos-score" aria-hidden="true">{score.notes.map((note, index) => <i key={index} style={{ left: `${note.at / score.duration * 85}%`, width: `${Math.max(3, note.duration / score.duration * 24)}%`, bottom: `${12 + (Math.log2(note.frequency / 40) * 12) % 66}%`, opacity: note.wave === "noise" ? 0.45 : 0.85 }} />)}</div>
    <Button variant="ghost" disabled={volume <= 0} onClick={() => {
      if (timeout.current) clearTimeout(timeout.current);
      audio.stop(cue);
      if (playing) { timeout.current = null; setPlaying(false); return; }
      audio.play(cue); setPlaying(true);
      timeout.current = setTimeout(() => { setPlaying(false); timeout.current = null; }, score.duration * 1000);
    }} data-testid={`cos-sound-${cosmetic.id}`}>{volume <= 0 ? t("store.cos.soundMuted") : playing ? t("store.cos.stopSound") : t("store.cos.playSound")}</Button>
  </div>;
}

export function NonModelCosmeticPreview({ cosmetic }: { cosmetic: CosmeticView }) {
  const t = useT(), account = useSession((s) => s.account);
  const name = account?.name ?? t("store.cos.previewName");
  if (cosmetic.kind === "march_trail") return <CosmeticModelPreview cosmetic={cosmetic} />;
  return <div className={`cos-nonmodel cos-nonmodel-${cosmetic.kind}`} data-testid={`cos-preview-${cosmetic.id}`} aria-label={pick(cosmetic.name)}>
    {cosmetic.kind === "frame" ? <CosmeticFrame id={cosmetic.id} colour={cosmetic.colour ?? "#fff"} /> : null}
    {cosmetic.kind === "name_colour" ? <div className="cos-name-demo"><span className="presence-dot on" /><strong style={{ color: cosmetic.colour ?? undefined }}>{name}</strong><span>{t("store.cos.previewMessage")}</span></div> : null}
    {cosmetic.kind === "chat_bubble" ? <div className="chat-msg cos-chat-demo" data-bubble={cosmetic.id}><strong>{name}</strong><div>{t("store.cos.previewMessage")}</div></div> : null}
    {cosmetic.kind === "emote_pack" ? <div className="cos-emote-pack">{EMOTES.filter((e) => e.pack === cosmetic.id).map((e) => <EmoteImage key={e.id} id={e.id} size={54} />)}</div> : null}
    {cosmetic.kind === "stinger" ? <StingerPreview cosmetic={cosmetic} /> : null}
  </div>;
}
